import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { 
  User, Mail, Camera, Save, Shield, Bell, 
  CreditCard, Sparkles, Eye, BarChart3, Key, 
  Trash2, AlertTriangle, ExternalLink, DollarSign,
  BookOpen, Calendar, FileText, Download, ChevronDown, ChevronUp
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/auth';
import { usersApi, DeletionCheckResponse, uploadsApi } from '../../lib/api';
import { InlineLoader } from '../../components/ui/PremiumLoader';
import {
  hasAnalyticsConsent,
  grantConsent,
  revokeConsent,
} from '../../lib/posthog';
import { resetPassword, isGoogleUser, logout as firebaseLogout } from '../../lib/firebase';

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  bio: z.string().max(500).optional(),
  avatarUrl: z.string().url().optional().or(z.literal('')),
});

type ProfileForm = z.infer<typeof profileSchema>;

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
};

const tabs = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'privacy', label: 'Privacy', icon: Eye },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'billing', label: 'Billing', icon: CreditCard },
];

export default function SettingsPage() {
  const { user, refreshUser, logout } = useAuthStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(hasAnalyticsConsent());
  
  // Security tab state
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);
  
  // Account deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isCheckingDeletion, setIsCheckingDeletion] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletionBlockedReason, setDeletionBlockedReason] = useState<{
    blocked: boolean;
    reason?: string;
    pendingBalance?: number;
  } | null>(null);
  const [studentDeletionData, setStudentDeletionData] = useState<DeletionCheckResponse | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showDocuments, setShowDocuments] = useState(false);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  // Check if user is using Google sign-in
  useEffect(() => {
    setIsGoogleAccount(isGoogleUser());
  }, []);

  // Handle password reset email
  const handleSendPasswordReset = async () => {
    if (!user?.email) return;
    
    setIsSendingReset(true);
    try {
      await resetPassword(user.email);
      setResetEmailSent(true);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send reset email';
      toast.error(message);
    } finally {
      setIsSendingReset(false);
    }
  };

  // Check if account can be deleted
  const handleCheckDeletion = async () => {
    setIsCheckingDeletion(true);
    try {
      const response = await usersApi.checkDeletion();
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // For students, store the full data for displaying warnings
        if (user?.role === 'STUDENT') {
          setStudentDeletionData(data);
          setDeletionBlockedReason(null);
          setShowDeleteModal(true);
        } else {
          // Professor flow
          if (data.canDelete) {
            setDeletionBlockedReason(null);
            setStudentDeletionData(null);
            setShowDeleteModal(true);
          } else {
            setDeletionBlockedReason({
              blocked: true,
              reason: data.reason,
              pendingBalance: data.pendingBalance,
            });
            setStudentDeletionData(null);
            setShowDeleteModal(true);
          }
        }
      }
    } catch {
      toast.error('Failed to check account status');
    } finally {
      setIsCheckingDeletion(false);
    }
  };

  // Handle document download
  const handleDownloadDocument = async (fileUrl: string, fileName: string) => {
    setIsDownloading(fileName);
    try {
      await uploadsApi.downloadFile(fileUrl, fileName);
      toast.success(`Downloaded: ${fileName}`);
    } catch {
      toast.error('Failed to download file');
    } finally {
      setIsDownloading(null);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    
    setIsDeleting(true);
    try {
      const response = await usersApi.deleteAccount();
      if (response.data.success) {
        toast.success('Your account has been deleted');
        // Sign out from Firebase and clear local state
        await firebaseLogout();
        await logout();
        navigate('/');
      } else {
        toast.error(response.data.error || 'Failed to delete account');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete account';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
  });

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        bio: user.bio || '',
        avatarUrl: user.avatarUrl || '',
      });
    }
  }, [user, reset]);

  const onSubmitProfile = async (data: ProfileForm) => {
    setIsSaving(true);
    try {
      const response = await usersApi.updateProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        bio: data.bio || undefined,
        avatarUrl: data.avatarUrl || undefined,
      });
      
      if (response.data.success) {
        await refreshUser();
        toast.success('Profile updated successfully');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial="initial"
      animate="animate"
      className="max-w-4xl mx-auto"
    >
      {/* Header */}
      <motion.div variants={fadeUp} transition={{ duration: 0.4 }} className="mb-8">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-aurora" />
          <span className="text-xs font-medium text-aurora uppercase tracking-wider">Settings</span>
        </div>
        <h1 className="text-2xl font-bold text-white">Account Settings</h1>
        <p className="mt-1 text-white/50 text-sm">
          Manage your account preferences and settings
        </p>
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Tabs */}
        <motion.div 
          variants={fadeUp} 
          transition={{ duration: 0.4, delay: 0.05 }}
          className="lg:w-48 flex-shrink-0 overflow-x-auto scrollbar-hide -mx-4 px-4 lg:mx-0 lg:px-0"
        >
          <nav className="flex lg:flex-col gap-1 min-w-max lg:min-w-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 lg:px-4 py-2.5 rounded-lg text-sm font-medium 
                         transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gradient-aurora text-white shadow-glow-aurora'
                    : 'text-white/50 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                <tab.icon className="w-4 h-4 flex-shrink-0" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </motion.div>

        {/* Main Content */}
        <motion.div 
          variants={fadeUp} 
          transition={{ duration: 0.4, delay: 0.1 }}
          className="flex-1"
        >
          {activeTab === 'profile' && (
            <form onSubmit={handleSubmit(onSubmitProfile)} className="space-y-6">
              <div className="card">
                <h2 className="text-lg font-semibold text-white mb-6">Profile Information</h2>

                {/* Avatar */}
                <div className="flex items-center gap-5 mb-6 pb-6 border-b border-white/[0.06]">
                  <div className="relative">
                    {user?.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt={user.firstName}
                        className="w-20 h-20 rounded-2xl object-cover ring-4 ring-aurora/20"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-2xl bg-gradient-aurora flex items-center justify-center 
                                    text-white text-2xl font-bold">
                        {user?.firstName.charAt(0)}
                      </div>
                    )}
                    <button
                      type="button"
                      className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-void-300 border border-white/10 
                               flex items-center justify-center hover:bg-void-400 transition-colors"
                    >
                      <Camera className="w-4 h-4 text-white/60" />
                    </button>
                  </div>
                  <div>
                    <p className="text-white font-medium">Profile Photo</p>
                    <p className="text-white/40 text-sm mt-0.5">
                      JPG, PNG or GIF. Max 5MB.
                    </p>
                  </div>
                </div>

                {/* Name fields - masked from analytics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 ph-no-capture">
                  <div>
                    <label htmlFor="firstName" className="label">First Name</label>
                    <input
                      id="firstName"
                      type="text"
                      {...register('firstName')}
                      className={`input ${errors.firstName ? 'input-error' : ''}`}
                      data-ph-no-capture
                    />
                    {errors.firstName && (
                      <p className="mt-1.5 text-xs text-error">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="lastName" className="label">Last Name</label>
                    <input
                      id="lastName"
                      type="text"
                      {...register('lastName')}
                      className={`input ${errors.lastName ? 'input-error' : ''}`}
                      data-ph-no-capture
                    />
                    {errors.lastName && (
                      <p className="mt-1.5 text-xs text-error">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                {/* Email (read-only) - masked from analytics */}
                <div className="mb-4 ph-no-capture">
                  <label className="label">Email</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="input pl-10 opacity-60 cursor-not-allowed"
                      data-ph-no-capture
                    />
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  </div>
                  <p className="mt-1.5 text-xs text-white/30">Email cannot be changed</p>
                </div>

                {/* Bio */}
                <div className="mb-4">
                  <label htmlFor="bio" className="label">Bio</label>
                  <textarea
                    id="bio"
                    {...register('bio')}
                    rows={4}
                    className="input resize-none"
                    placeholder="Tell us about yourself..."
                  />
                  {errors.bio && (
                    <p className="mt-1.5 text-xs text-error">{errors.bio.message}</p>
                  )}
                </div>

                {/* Save button */}
                <div className="flex justify-end pt-4 border-t border-white/[0.06]">
                  <button
                    type="submit"
                    disabled={!isDirty || isSaving}
                    className="btn-primary"
                  >
                    {isSaving ? (
                      <>
                        <InlineLoader className="mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}

          {activeTab === 'privacy' && (
            <div className="space-y-6">
              <div className="card">
                <h2 className="text-lg font-semibold text-white mb-6">Privacy Settings</h2>
                
                {/* Analytics Toggle */}
                <div className="flex items-start gap-4 pb-6 border-b border-white/[0.06]">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-aurora/10 
                                flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-aurora" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-white font-medium">Analytics & Session Recording</h3>
                      <button
                        onClick={() => {
                          if (analyticsEnabled) {
                            revokeConsent();
                            setAnalyticsEnabled(false);
                            toast.success('Analytics disabled');
                          } else {
                            grantConsent();
                            setAnalyticsEnabled(true);
                            toast.success('Analytics enabled');
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          analyticsEnabled 
                            ? 'bg-aurora' 
                            : 'bg-white/20'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            analyticsEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    <p className="text-white/50 text-sm leading-relaxed">
                      Help us improve Ulearning by allowing us to collect anonymous usage data 
                      and session recordings. All sensitive information is automatically masked.
                    </p>
                  </div>
                </div>

                {/* Privacy Information */}
                <div className="pt-6 space-y-4">
                  <h3 className="text-white/80 font-medium text-sm">What we collect:</h3>
                  <ul className="text-white/50 text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-aurora mt-1">•</span>
                      <span>Page views and navigation patterns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-aurora mt-1">•</span>
                      <span>Button clicks and form submissions (inputs are masked)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-aurora mt-1">•</span>
                      <span>Session recordings with all sensitive data masked</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-aurora mt-1">•</span>
                      <span>Your role (student/professor) for feature improvements</span>
                    </li>
                  </ul>
                  
                  <h3 className="text-white/80 font-medium text-sm pt-4">What we never collect:</h3>
                  <ul className="text-white/50 text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-coral mt-1">✕</span>
                      <span>Passwords or authentication tokens</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-coral mt-1">✕</span>
                      <span>Payment or credit card information</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-coral mt-1">✕</span>
                      <span>Personal messages or private content</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-coral mt-1">✕</span>
                      <span>Email addresses or contact information</span>
                    </li>
                  </ul>
                </div>

                {/* Privacy Policy Link */}
                <div className="pt-6 mt-6 border-t border-white/[0.06]">
                  <Link 
                    to="/privacy" 
                    className="text-aurora hover:text-aurora-light text-sm transition-colors"
                  >
                    Read our full Privacy Policy →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">Notification Preferences</h2>
              <p className="text-white/40">Notification settings coming soon...</p>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              {/* Password Section */}
              <div className="card">
                <h2 className="text-lg font-semibold text-white mb-6">Password & Authentication</h2>
                
                {isGoogleAccount ? (
                  // Google users - explain they need to update their Google account
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-500/10 
                                  flex items-center justify-center">
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-medium mb-1">Google Account</h3>
                      <p className="text-white/50 text-sm leading-relaxed mb-4">
                        You signed in with your Google account. To change your password, 
                        you need to update it through your Google Account settings.
                      </p>
                      <a
                        href="https://myaccount.google.com/security"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-aurora hover:text-aurora-light 
                                 text-sm font-medium transition-colors"
                      >
                        Manage Google Account Security
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                ) : (
                  // Email/password users - show reset option
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-aurora/10 
                                  flex items-center justify-center">
                      <Key className="w-5 h-5 text-aurora" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-medium mb-1">Reset Password</h3>
                      <p className="text-white/50 text-sm leading-relaxed mb-4">
                        We'll send a password reset link to your email address: <span className="text-white/70">{user?.email}</span>
                      </p>
                      
                      {resetEmailSent ? (
                        <div className="flex items-center gap-2 text-success text-sm">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Password reset email sent! Check your inbox.
                        </div>
                      ) : (
                        <button
                          onClick={handleSendPasswordReset}
                          disabled={isSendingReset}
                          className="btn-secondary text-sm"
                        >
                          {isSendingReset ? (
                            <>
                              <InlineLoader className="mr-2" />
                              Sending...
                            </>
                          ) : (
                            'Send Reset Email'
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Delete Account Section */}
              <div className="card border-error/20">
                <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-error" />
                  Danger Zone
                </h2>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-error/10 
                                flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-error" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-medium mb-1">Delete Account</h3>
                    <p className="text-white/50 text-sm leading-relaxed mb-4">
                      Permanently delete your account and all associated data. This action cannot be undone.
                    </p>
                    <ul className="text-white/40 text-sm mb-4 space-y-1">
                      <li>• All your personal information will be deleted</li>
                      <li>• Your course enrollments and progress will be removed</li>
                      {user?.role === 'PROFESSOR' && (
                        <>
                          <li>• Your courses will be unpublished and archived</li>
                          <li>• Enrolled students will retain access to course materials</li>
                        </>
                      )}
                    </ul>
                    
                    <button
                      onClick={handleCheckDeletion}
                      disabled={isCheckingDeletion}
                      className="px-4 py-2 bg-error/10 hover:bg-error/20 text-error 
                               rounded-lg font-medium text-sm transition-colors"
                    >
                      {isCheckingDeletion ? (
                        <>
                          <InlineLoader className="mr-2" />
                          Checking...
                        </>
                      ) : (
                        'Delete My Account'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Account Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-lg card border border-white/10 max-h-[90vh] overflow-y-auto"
              >
                {deletionBlockedReason?.blocked ? (
                  // Account deletion is blocked (professor has pending balance)
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-warning" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Cannot Delete Account</h3>
                        <p className="text-white/50 text-sm">Pending balance requires payout</p>
                      </div>
                    </div>
                    
                    <div className="bg-warning/5 border border-warning/20 rounded-xl p-4 mb-6">
                      <p className="text-white/80 text-sm mb-3">
                        You have a pending balance of <span className="font-semibold text-warning">
                          ${deletionBlockedReason.pendingBalance?.toFixed(2)}
                        </span> that needs to be paid out before you can delete your account.
                      </p>
                      <p className="text-white/60 text-sm">
                        Please go to your Earnings page to request a payout of your remaining balance. 
                        Once your balance is $0.00, you can delete your account.
                      </p>
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowDeleteModal(false);
                          setDeletionBlockedReason(null);
                        }}
                        className="flex-1 btn-secondary"
                      >
                        Close
                      </button>
                      <Link 
                        to="/professor/earnings" 
                        className="flex-1 btn-primary text-center"
                        onClick={() => setShowDeleteModal(false)}
                      >
                        Go to Earnings
                      </Link>
                    </div>
                  </>
                ) : user?.role === 'STUDENT' && studentDeletionData ? (
                  // Student deletion with warnings
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-error" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Delete Your Account?</h3>
                        <p className="text-white/50 text-sm">This action is permanent</p>
                      </div>
                    </div>

                    {/* Warnings Section */}
                    {(studentDeletionData.hasActiveEnrollments || studentDeletionData.hasDocuments || studentDeletionData.hasOngoingCourses) && (
                      <div className="space-y-3 mb-6">
                        {/* Active Enrollments Warning */}
                        {studentDeletionData.hasActiveEnrollments && (
                          <div className="bg-warning/5 border border-warning/20 rounded-xl p-4">
                            <div className="flex items-start gap-3">
                              <BookOpen className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="font-medium text-warning text-sm">Active Enrollments</h4>
                                <p className="text-white/60 text-sm mt-1">
                                  You have {studentDeletionData.enrolledCoursesCount} active course{studentDeletionData.enrolledCoursesCount !== 1 ? 's' : ''} that will be cancelled.
                                </p>
                                {studentDeletionData.enrolledCourses && studentDeletionData.enrolledCourses.length > 0 && (
                                  <ul className="mt-2 space-y-1">
                                    {studentDeletionData.enrolledCourses.slice(0, 3).map(course => (
                                      <li key={course.id} className="text-xs text-white/50 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-warning/50"></span>
                                        {course.title} ({Math.round(course.progress)}% complete)
                                      </li>
                                    ))}
                                    {studentDeletionData.enrolledCourses.length > 3 && (
                                      <li className="text-xs text-white/40">
                                        ... and {studentDeletionData.enrolledCourses.length - 3} more
                                      </li>
                                    )}
                                  </ul>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Upcoming Sessions Warning */}
                        {studentDeletionData.hasOngoingCourses && (
                          <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4">
                            <div className="flex items-start gap-3">
                              <Calendar className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="font-medium text-purple-400 text-sm">Upcoming Live Sessions</h4>
                                <p className="text-white/60 text-sm mt-1">
                                  You have {studentDeletionData.upcomingSessionsCount} upcoming live session{studentDeletionData.upcomingSessionsCount !== 1 ? 's' : ''} that you will miss.
                                </p>
                                {studentDeletionData.upcomingSessions && studentDeletionData.upcomingSessions.length > 0 && (
                                  <ul className="mt-2 space-y-1">
                                    {studentDeletionData.upcomingSessions.slice(0, 3).map(session => (
                                      <li key={session.id} className="text-xs text-white/50 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-purple-400/50"></span>
                                        {session.title} - {new Date(session.scheduledAt).toLocaleDateString()}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Documents Warning with Download */}
                        {studentDeletionData.hasDocuments && (
                          <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
                            <div className="flex items-start gap-3">
                              <FileText className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <h4 className="font-medium text-blue-400 text-sm">Course Documents</h4>
                                  <button
                                    onClick={() => setShowDocuments(!showDocuments)}
                                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                                  >
                                    {showDocuments ? (
                                      <>Hide <ChevronUp className="w-3 h-3" /></>
                                    ) : (
                                      <>Show <ChevronDown className="w-3 h-3" /></>
                                    )}
                                  </button>
                                </div>
                                <p className="text-white/60 text-sm mt-1">
                                  You have access to {studentDeletionData.documentsCount} document{studentDeletionData.documentsCount !== 1 ? 's' : ''}. 
                                  <span className="text-blue-400"> Download them before deleting your account.</span>
                                </p>
                                
                                {/* Documents List */}
                                {showDocuments && studentDeletionData.documentsByCourse && (
                                  <div className="mt-3 space-y-3 max-h-48 overflow-y-auto">
                                    {Object.entries(studentDeletionData.documentsByCourse).map(([courseId, courseData]) => (
                                      <div key={courseId} className="border-t border-white/5 pt-2">
                                        <p className="text-xs font-medium text-white/70 mb-2">{courseData.courseTitle}</p>
                                        <div className="space-y-1">
                                          {courseData.documents.map(doc => (
                                            <div key={doc.id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                                              <div className="flex-1 min-w-0">
                                                <p className="text-xs text-white/80 truncate">{doc.title || doc.fileName}</p>
                                                <p className="text-[10px] text-white/40">{formatFileSize(doc.fileSize)}</p>
                                              </div>
                                              <button
                                                onClick={() => handleDownloadDocument(doc.fileUrl, doc.fileName)}
                                                disabled={isDownloading === doc.fileName}
                                                className="flex-shrink-0 p-1.5 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors disabled:opacity-50"
                                              >
                                                {isDownloading === doc.fileName ? (
                                                  <InlineLoader />
                                                ) : (
                                                  <Download className="w-3.5 h-3.5" />
                                                )}
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="bg-error/5 border border-error/20 rounded-xl p-4 mb-6">
                      <p className="text-white/80 text-sm mb-3">
                        All of your data will be permanently deleted, including:
                      </p>
                      <ul className="text-white/60 text-sm space-y-1">
                        <li>• Your profile and account settings</li>
                        <li>• Course enrollments and learning progress</li>
                        <li>• Reviews and activity history</li>
                      </ul>
                    </div>
                    
                    <div className="mb-6">
                      <label className="label">
                        Type <span className="text-error font-mono">DELETE</span> to confirm
                      </label>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="DELETE"
                        className="input font-mono"
                      />
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowDeleteModal(false);
                          setDeleteConfirmText('');
                          setStudentDeletionData(null);
                          setShowDocuments(false);
                        }}
                        className="flex-1 btn-secondary"
                        disabled={isDeleting}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                        className="flex-1 px-4 py-2 bg-error hover:bg-error/90 text-white 
                                 rounded-lg font-medium transition-colors disabled:opacity-50 
                                 disabled:cursor-not-allowed"
                      >
                        {isDeleting ? (
                          <>
                            <InlineLoader className="mr-2" />
                            Deleting...
                          </>
                        ) : (
                          'Delete Account'
                        )}
                      </button>
                    </div>
                  </>
                ) : (
                  // Professor or default confirm deletion
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-error" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Delete Your Account?</h3>
                        <p className="text-white/50 text-sm">This action is permanent</p>
                      </div>
                    </div>
                    
                    <div className="bg-error/5 border border-error/20 rounded-xl p-4 mb-6">
                      <p className="text-white/80 text-sm mb-3">
                        All of your data will be permanently deleted, including:
                      </p>
                      <ul className="text-white/60 text-sm space-y-1">
                        <li>• Your profile and account settings</li>
                        <li>• Course enrollments and learning progress</li>
                        <li>• Reviews and activity history</li>
                        {user?.role === 'PROFESSOR' && (
                          <li>• Your courses (students will retain access)</li>
                        )}
                      </ul>
                    </div>
                    
                    <div className="mb-6">
                      <label className="label">
                        Type <span className="text-error font-mono">DELETE</span> to confirm
                      </label>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="DELETE"
                        className="input font-mono"
                      />
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowDeleteModal(false);
                          setDeleteConfirmText('');
                        }}
                        className="flex-1 btn-secondary"
                        disabled={isDeleting}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                        className="flex-1 px-4 py-2 bg-error hover:bg-error/90 text-white 
                                 rounded-lg font-medium transition-colors disabled:opacity-50 
                                 disabled:cursor-not-allowed"
                      >
                        {isDeleting ? (
                          <>
                            <InlineLoader className="mr-2" />
                            Deleting...
                          </>
                        ) : (
                          'Delete Account'
                        )}
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="card">
              <h2 className="text-lg font-semibold text-white mb-4">Billing & Payments</h2>
              <p className="text-white/40">Billing settings coming soon...</p>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

